import { computed, defineAsyncComponent, defineComponent, inject, nextTick, onMounted, ref, } from "vue";
import { useRoute, useRouter } from "vue-router";
import useQuasar from 'quasar/src/composables/use-quasar/use-quasar.js';
import { useAccountStore } from 'src/stores/account';
import { useCentralStore } from "src/stores/central";
import { useViewStore } from "src/stores/view";
import { debounceTime, tap } from "rxjs/operators";
import { rxInitSubjectCallback, useObservable } from "src/plugins/rxjs/base";
import { switchMap } from "rxjs";
import appMultiColoredIcon from "src/components/icon/appMultiColoredIcon.vue";
import photoToggle from "./photoButton.vue";
import headerCashback from "src/layouts/components/header/headerCashback.vue";
import searchBrowseBtn from "src/layouts/components/header/searchBrowseBtn.vue";
import verificationAndDelBtn from "src/layouts/components/header/verificationAndDelBtn.vue";
export default defineComponent({
    name: "desktop-header",
    components: {
        appMultiColoredIcon,
        headerCashback,
        photoToggle,
        searchBrowseBtn,
        verificationAndDelBtn,
        notifications: defineAsyncComponent(() => import(
        /* webpackChunkName: "Notifications" */
        "src/features/notification/notifications.vue")),
        inbox: defineAsyncComponent(() => import(
        /* webpackChunkName: "Chat Inbox" */
        "src/features/messaging/inbox/firebaseInbox.vue")),
    },
    props: {
        homeHeader: Boolean,
    },
    setup() {
        const $q = useQuasar();
        const vueRoute = useRoute();
        const vueRouter = useRouter();
        const accountStore = useAccountStore();
        const centralStore = useCentralStore();
        const viewStore = useViewStore();
        const isOnline = computed(() => centralStore.online);
        /****
         * Misc
         ****/
        const quickNavMenu = ref(false);
        /**
         * Go to host list with us
         */
        function goToListWithUs() {
            vueRouter.push({ name: "Rent Out Spaces Flexibly" });
        }
        /****
         * Inbox
         ****/
        const unread = computed(() => accountStore.unreadChat);
        const showInbox = computed({
            get: () => {
                return viewStore.inboxMenu;
            },
            set: (value) => {
                if (!value && (vueRoute.query.popup === "inbox")) {
                    const query = { ...vueRoute.query };
                    delete query.popup;
                    vueRouter.push({ query });
                }
                viewStore.toggleInboxMenu(value);
            },
        });
        /****
         * Notifications
         ****/
        const unreadNotifications = computed(() => accountStore.unreadNotifications);
        const showNotification = computed({
            get: () => {
                return viewStore.notificationMenu;
            },
            set: (value) => {
                if (!value && (vueRoute.query.popup === "notifications")) {
                    const query = { ...vueRoute.query };
                    delete query.popup;
                    vueRouter.push({ query });
                }
                viewStore.toggleNotificationMenu(value);
            },
        });
        /****
         * Drawer
         ****/
        const drawer = computed({
            get: () => {
                return viewStore.drawer;
            },
            set: (value) => {
                if (value) {
                    $q.sessionStorage.set("openingChat", true);
                }
                viewStore.toggleDrawer(value);
            },
        });
        function toggleDrawer(type) {
            drawer.value = !drawer.value;
            if (type) {
                if (type === "chat") {
                    showInbox.value = false;
                }
                viewStore.toggleDrawerTab(type);
            }
        }
        /*****
         * User
         *****/
        const user = computed(() => accountStore.user);
        const isOwner = inject("isOwner", false);
        /**
         * Show login dialog
         * @param param0 dialog options
         */
        function requireLogin({ required, tab = 'login' }) {
            viewStore.requireLogin({ required, tab });
        }
        /***
         * Go to inbox/notif
         ****/
        const { subject: mountWatcher$ } = rxInitSubjectCallback();
        useObservable(mountWatcher$.pipe(switchMap(() => nextTick()), debounceTime(100), tap(() => {
            if (!vueRoute.query.popup)
                return;
            if (!user.value) {
                requireLogin({ required: true });
            }
            else {
                if (vueRoute.query.popup === "notifications") {
                    showNotification.value = true;
                }
                else if (vueRoute.query.popup === "inbox") {
                    showInbox.value = true;
                }
            }
        })));
        onMounted(() => {
            mountWatcher$.next("");
        });
        return {
            isOnline,
            quickNavMenu,
            goToListWithUs,
            unread,
            showInbox,
            unreadNotifications,
            showNotification,
            user,
            isOwner,
            requireLogin,
            drawer,
            toggleDrawer,
        };
    },
});
